import React from "react";
import { Stack, Text, Title } from "@mantine/core";
import moment from "moment";
import { RunLedgerReportResponse } from "../../../../types/reportsV2";
import LedgerTable from "./LedgerTable";
import styles from "./SummaryLedgers.module.css";

export interface SummaryLedgersProps {
  /** Report display name: the heading of a printed copy (on screen the
   *  report header already shows it). */
  name: string;
  data: RunLedgerReportResponse;
  compact?: boolean;
  /** Print: the name as a heading, no info icons. */
  plain?: boolean;
}

const LONG_DATE = "MMMM D, YYYY";

/** "October 26, 2026" for one day, "October 1, 2026 – October 7, 2026" for a
 *  range (the dates the run actually covered). */
export function ledgerPeriodLabel(start: string, end: string): string {
  const from = moment(start, "YYYY-MM-DD");
  const to = moment(end, "YYYY-MM-DD");
  if (!from.isValid() || !to.isValid()) return "";
  if (from.isSame(to, "day")) return from.format(LONG_DATE);
  return `${from.format(LONG_DATE)} – ${to.format(LONG_DATE)}`;
}

/**
 * A summary report's result: the period it covers (and, printed, the report
 * name), centred like a statement, then each ledger in order. Shared by the
 * desktop and phone layouts and the print copy.
 */
export default function SummaryLedgers({
  name,
  data,
  compact = false,
  plain = false,
}: SummaryLedgersProps) {
  const { start, end } = data.date_range;
  return (
    <Stack gap={compact ? "sm" : "md"}>
      <Stack gap={2} className={styles.heading}>
        {plain && (
          <Title order={2} className={styles.title}>
            {name}
          </Title>
        )}
        <Text className={styles.period}>{ledgerPeriodLabel(start, end)}</Text>
      </Stack>
      {data.ledgers.map((ledger) => (
        <LedgerTable
          key={ledger.key}
          ledger={ledger}
          compact={compact}
          plain={plain}
        />
      ))}
    </Stack>
  );
}
